import React from 'react';
import PropTypes from 'prop-types';
import { noop, pick, orderBy, get } from 'lodash';
import classnames from 'classnames';
import { injectIntl, FormattedMessage } from 'react-intl';

import { Icon, MaterialHint } from 'shared/components';

import { CREATED } from 'modules/common/constants/conversationStatuses';
import { SCHEDULED, WALK_IN } from 'modules/queues/constants/queueTypes';
import * as appointmentStatuses from 'modules/common/constants/appointmentStatuses';

import theme from './AppointmentCard.scss';

import {
  AppointmentLastConversation,
  AppointmentParticipantsInfo,
  AppointmentSchedule,
  AppointmentStatusBar,
} from './elements';


const propTypes = {
  style: PropTypes.object,
  isF2F: PropTypes.bool.isRequired,
  assignedToGroup: PropTypes.bool,
  status: PropTypes.string.isRequired,
  filePrerequisiteStatus: PropTypes.string,
  appointmentId: PropTypes.string.isRequired,
  filesPrerequisitesConfig: PropTypes.object,
  expectedTime: PropTypes.object,
  overdue: PropTypes.bool.isRequired,
  subject: PropTypes.shape({
    policyId: PropTypes.string,
    subjectName: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    gender: PropTypes.string,
  }),
  groupInfo: PropTypes.shape({
    isGroup: PropTypes.bool,
    index: PropTypes.number,
    total: PropTypes.number,
  }),
  permissions: PropTypes.shape({
    canSeeProvider: PropTypes.bool.isRequired,
    canSeeCustomer: PropTypes.bool.isRequired,
    canSeeSubjects: PropTypes.bool.isRequired,
    canSeeSchedule: PropTypes.bool.isRequired,
    canSeeStatus: PropTypes.bool.isRequired,
  }).isRequired,
  provider: PropTypes.shape({
    id: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
    avatar: PropTypes.string,
  }),
  providerGroupSpecialty: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  customer: PropTypes.shape({
    id: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
    avatar: PropTypes.string,
  }).isRequired,
  schedules: PropTypes.array,
  summaries: PropTypes.array,
  attachments: PropTypes.array,
  prescriptions: PropTypes.array,
  conversations: PropTypes.array,
  faceToFaceType: PropTypes.string,
  showAppointmentInfo: PropTypes.func,
  serviceName: PropTypes.string,
  closeTime: PropTypes.string,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  queueType: PropTypes.oneOf([WALK_IN, SCHEDULED]),
  caseConfig: PropTypes.object.isRequired,
};

const defaultProps = {
  style: {},
  assignedToGroup: false,
  serviceName: null,
  showAppointmentInfo: noop,
  subject: null,
  provider: null,
  providerGroupSpecialty: null,
  schedules: [],
  prescriptions: [],
  attachments: [],
  conversations: [],
  summaries: [],
  closeTime: undefined,
  queueType: null,
  expectedTime: null,
  groupInfo: null,
  filePrerequisiteStatus: null,
  filesPrerequisitesConfig: null,
  faceToFaceType: null,
};

const AppointmentCard = ({
  isF2F,
  style,
  intl,
  status,
  provider,
  providerGroupSpecialty,
  customer,
  subject,
  overdue,
  schedules,
  closeTime,
  queueType,
  groupInfo,
  caseConfig,
  serviceName,
  permissions,
  attachments,
  appointmentId,
  conversations,
  faceToFaceType,
  assignedToGroup,
  showAppointmentInfo,
  filePrerequisiteStatus,
  filesPrerequisitesConfig,
}) => {
  const filteredConversations = conversations.filter(conversation => conversation.status !== CREATED);
  const sortedConversations = orderBy(filteredConversations, ['start'], ['desc']);

  const lastConversation = pick(sortedConversations[0], ['start', 'end', 'status']);
  const isClosed = appointmentStatuses.CLOSED_APPOINTMENT_STATUSES.includes(status);

  return (
    <button
      id={ `appointmentCard-${ appointmentId }` }
      style={ style }
      className={ classnames(theme.root, {
        [theme.closed]: isClosed,
      }) }
      onClick={ () => showAppointmentInfo(appointmentId) }
    >
      <div className={ theme.mainInfo }>
        <div className={ theme.leftContainer }>
          <AppointmentParticipantsInfo { ...{
            provider,
            providerGroupSpecialty,
            assignedToGroup,
            customer,
            subject,
            serviceName,
            permissions,
          } }
          />
          <AppointmentSchedule { ...{
            intl,
            isF2F,
            status,
            overdue,
            schedules,
            closeTime,
            queueType,
            permissions,
            appointmentId,
            faceToFaceType,
            prerequisitesIncomplete: (filesPrerequisitesConfig?.enabled && filePrerequisiteStatus === 'INCOMPLETE') || get(caseConfig, 'casePrerequisite.status') === 'INCOMPLETE',
          } }
          />
        </div>
        <If condition={ permissions.canSeeConversation }>
          <div className={ classnames(theme.rightContainer) }>
            <AppointmentLastConversation { ...{ ...lastConversation, intl } } />
            <If condition={ groupInfo.isGroup }>
              <div className={ classnames(theme.rightContainer, theme.groupInfo) }>
                <MaterialHint
                  title={ intl.formatMessage({
                    id: 'Appointments.AppointmentCard.GroupOfAppointments.Label.Tooltip',
                    defaultMessage: '** Appointment is part of a group',
                  }) }
                >
                  <span style={{ display: 'flex' }}>
                    <Icon className={ theme.iconGroup } name="layers" />

                    <FormattedMessage
                      id="Appointments.AppointmentCard.GroupOfAppointments.Label"
                      defaultMessage="** {index} out of {count}"
                      values={{ index: groupInfo.index, count: groupInfo.total }}
                    />
                  </span>
                </MaterialHint>
              </div>
            </If>
          </div>
        </If>
      </div>

      <AppointmentStatusBar
        { ...{
          attachments,
          permissions,
          filePrerequisiteStatus,
          filesPrerequisitesConfig,
          closeTime,
          casePrerequisitesConfig: get(caseConfig, 'casePrerequisite'),
        } }
      />
    </button>
  );
};

AppointmentCard.propTypes = propTypes;
AppointmentCard.defaultProps = defaultProps;

export default injectIntl(AppointmentCard);
